import { SectionLabel } from "@/components/yanre/SectionLabel";
import { Reveal, RevealLines } from "@/components/yanre/Reveal";
import { useLocale } from "@/i18n/LocaleContext";

// GLOBAL REUSABLE COMPONENT — factory-direct purchase process.
// Desktop: horizontal journey on a rail. Mobile: vertical timeline.
export const PROCESS_STEPS = [
  { num: "01", key: "pp.1", title: "Project Brief", body: "Customer explains space, concept, investment and schedule." },
  { num: "02", key: "pp.2", title: "YANRE Consulting", body: "Equipment selection, technical guidance and project configuration." },
  { num: "03", key: "pp.3", title: "Factory Contract", body: "Final proposal and official equipment order directly with YANRE FITNESS." },
  { num: "04", key: "pp.4", title: "30% Factory Payment", body: "Standard deposit paid directly to YANRE FITNESS and the order enters production.", payment: true },
  { num: "05", key: "pp.5", title: "Production", body: "Equipment manufacturing and preparation." },
  { num: "06", key: "pp.6", title: "70% Factory Payment", body: "Standard balance paid directly to YANRE FITNESS after production and before shipment, according to the official contract.", payment: true },
  { num: "07", key: "pp.7", title: "International Shipping", body: "Customer may use their own logistics provider or request logistics coordination." },
  { num: "08", key: "pp.8", title: "After-Sales", body: "Ongoing support and factory-backed warranty after delivery." },
];

export const PurchaseProcess = ({ index }) => {
  const { t } = useLocale();
  return (
    <section id="process" className="section-dark relative overflow-hidden border-t border-carbon-700" data-testid="purchase-process-section">
      <div className="grid-lines absolute inset-0 opacity-40" />
      <div className="container-edge relative py-16 lg:py-24">
        <Reveal>
          <SectionLabel index={index}>Factory Direct Purchase Process</SectionLabel>
        </Reveal>
        <h2 className="mt-8 max-w-4xl font-display text-4xl font-extrabold uppercase leading-[0.92] tracking-tight text-white sm:text-5xl lg:text-6xl">
          <RevealLines
            lines={[
              t("pp.h1a", "A clear process."),
              <span key="p">
                {t("pp.h1b", "From project to after-sales.")}
              </span>,
            ]}
          />
        </h2>

        <div className="relative mt-14 lg:mt-20">
          {/* journey rails */}
          <div className="absolute bottom-4 left-5 top-4 w-px bg-carbon-700 rtl:left-auto rtl:right-5 lg:hidden" />
          <div className="absolute left-0 right-0 top-5 hidden h-px bg-carbon-700 lg:block" />
          <ol className="relative grid gap-10 lg:grid-cols-4 lg:gap-x-8 lg:gap-y-16" data-testid="process-steps">
            {PROCESS_STEPS.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.05}>
                <li className="relative pl-16 lg:pl-0 rtl:pl-0 rtl:pr-16 lg:rtl:pr-0">
                  <span
                    className={`absolute left-0 top-0 flex h-10 w-10 items-center justify-center border font-display text-sm font-extrabold rtl:left-auto rtl:right-0 lg:static lg:mb-7 ${
                      s.payment
                        ? "border-signal bg-signal text-white"
                        : "border-carbon-600 bg-carbon-900 text-titanium"
                    }`}
                    data-testid={s.payment ? `process-payment-${s.num}` : undefined}
                  >
                    {s.num}
                  </span>
                  <h3
                    className={`font-display text-lg font-bold uppercase leading-tight lg:mt-0 ${
                      s.payment ? "text-signal" : "text-white"
                    }`}
                  >
                    {t(`${s.key}.t`, s.title)}
                  </h3>
                  <p className="mt-2 max-w-xs text-sm leading-relaxed text-titanium/60">{t(`${s.key}.b`, s.body)}</p>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>

        <Reveal delay={0.15}>
          <div className="mt-14 border border-signal/40 bg-signal/5 p-6 sm:p-8" data-testid="process-payment-statement">
            <p className="font-display text-xl font-extrabold uppercase leading-tight text-white sm:text-2xl">
              {t("pp.payTitle", "100% of equipment payments are made directly to YANRE FITNESS.")}
            </p>
            <p className="mt-3 text-sm text-titanium/60">
              {t("pp.paySub", "No equipment payment is made to agents, consultants or third-party intermediaries.")}
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-6 font-mono text-[10px] uppercase leading-relaxed tracking-[0.15em] text-titanium/40">
            {t("pp.disclaimer", "Standard payment structure shown. Final commercial and payment terms are defined in the official YANRE FITNESS sales contract.")}
          </p>
        </Reveal>
      </div>
    </section>
  );
};
